import { useEffect, useState } from "react";
import { X, Loader2, User, Cake, Mail, Phone, Gift, Calendar, Euro, FileText, Save, Tag, TrendingUp, TrendingDown, Ticket, Plus } from "lucide-react";
import api from "@/lib/api";

/** Side-drawer with full customer history. Triggered by row click in /cms/customers. */
export default function CustomerDetailDrawer({ open, customerId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [notes, setNotes] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return undefined;
    setLoading(true); setData(null); setTab("bookings"); setNotesSaved(false);
    let cancelled = false;
    api.get(`/cms/customers/${customerId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setNotes(data.notes || "");
        setTagsInput((data.tags || []).join(", "));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, customerId]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      await api.put(`/cms/customers/${customerId}/notes`, { notes, tags });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2200);
      if (onUpdated) onUpdated();
    } finally { setSavingNotes(false); }
  };

  if (!open) return null;

  const showRevenue = data && "total_revenue" in data;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="customer-drawer">
      <div className="absolute inset-0 transition-opacity" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose} />
      <aside
        className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto shadow-2xl mv-fade-in"
        style={{ background: "var(--mv-bg)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--mv-border)", background: "rgba(253,251,247,0.92)", backdropFilter: "blur(20px)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--mv-primary)", color: "#fff" }}>
              <User className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="font-display text-xl truncate">{data?.name || "Lade…"}</p>
              {data?.email && <p className="text-xs opacity-70 truncate">{data.email}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--mv-surface-2)]" data-testid="customer-drawer-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </header>

        {loading || !data ? (
          <div className="p-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade Profil…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Quick facts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Fact icon={Gift} label="Treuepunkte" value={data.points_balance} />
              <Fact icon={Calendar} label="Termine" value={showRevenue ? data.total_bookings : data.bookings.filter((b) => ["confirmed","completed"].includes(b.status)).length} />
              {showRevenue && <Fact icon={Euro} label="Umsatz" value={`${(data.total_revenue || 0).toFixed(2)} €`} />}
              <Fact icon={Cake} label="Geburtstag" value={data.birthday ? formatDate(data.birthday) : "—"} />
            </div>

            {/* Tags chips */}
            {(data.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="customer-tags">
                {data.tags.map((t) => (
                  <span key={t} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                    <Tag className="w-3 h-3 inline mr-1" strokeWidth={1.6} /> {t}
                  </span>
                ))}
              </div>
            )}

            {/* Contact strip */}
            <div className="flex flex-wrap gap-3 text-sm">
              {data.email && <a href={`mailto:${data.email}`} className="flex items-center gap-1.5 hover:opacity-70"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {data.email}</a>}
              {data.phone && <a href={`tel:${data.phone}`} className="flex items-center gap-1.5 hover:opacity-70"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {data.phone}</a>}
            </div>

            {/* Tabs */}
            <div className="border-b flex gap-1 mt-2" style={{ borderColor: "var(--mv-border)" }}>
              <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testId="tab-bookings"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> Termine ({data.bookings.length})</TabBtn>
              <TabBtn active={tab === "loyalty"} onClick={() => setTab("loyalty")} testId="tab-loyalty"><Gift className="w-3.5 h-3.5" strokeWidth={1.5} /> Treuepunkte ({data.loyalty_transactions.length})</TabBtn>
              <TabBtn active={tab === "redemptions"} onClick={() => setTab("redemptions")} testId="tab-redemptions"><Ticket className="w-3.5 h-3.5" strokeWidth={1.5} /> Belohnungen ({data.redemptions.length})</TabBtn>
              <TabBtn active={tab === "notes"} onClick={() => setTab("notes")} testId="tab-notes"><FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Notizen</TabBtn>
            </div>

            <div data-testid={`customer-tab-${tab}`}>
              {tab === "bookings" && <BookingsTab bookings={data.bookings} showRevenue={showRevenue} />}
              {tab === "loyalty" && <LoyaltyTab transactions={data.loyalty_transactions} balance={data.points_balance} />}
              {tab === "redemptions" && <RedemptionsTab redemptions={data.redemptions} />}
              {tab === "notes" && (
                <NotesTab
                  notes={notes} setNotes={setNotes}
                  tagsInput={tagsInput} setTagsInput={setTagsInput}
                  onSave={saveNotes} saving={savingNotes} saved={notesSaved}
                />
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="mv-card p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="mv-label">{label}</p>
        <Icon className="w-3 h-3 opacity-40" strokeWidth={1.5} />
      </div>
      <p className="text-lg font-display">{value ?? "—"}</p>
    </div>
  );
}

function TabBtn({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors"
      style={{
        borderColor: active ? "var(--mv-primary)" : "transparent",
        color: active ? "var(--mv-primary)" : "var(--mv-text-muted)",
      }}
    >{children}</button>
  );
}

function BookingsTab({ bookings, showRevenue }) {
  if (bookings.length === 0) return <Empty icon={Calendar} text="Noch keine Termine." />;
  return (
    <ul className="divide-y" style={{ borderColor: "var(--mv-border)" }} data-testid="bookings-list">
      {bookings.map((b) => (
        <li key={b.id} className="py-3 flex items-start justify-between gap-3" data-testid={`booking-row-${b.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <p className="font-medium text-sm">{b.service_name}</p>
              <StatusPill status={b.status} />
              {b.created_by_admin && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded opacity-75" style={{ background: "var(--mv-surface-2)" }}>Walk-in</span>}
            </div>
            <p className="text-xs opacity-75">{formatDate(b.date)} · {b.time} {b.staff_name ? `· bei ${b.staff_name}` : ""} · {b.duration_minutes} Min</p>
            {b.notes && <p className="text-xs mt-1 opacity-60 italic">&bdquo;{b.notes}&ldquo;</p>}
          </div>
          {showRevenue && (
            <p className="text-sm font-display whitespace-nowrap tabular-nums">{b.price.toFixed(2)} €</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function LoyaltyTab({ transactions, balance }) {
  if (transactions.length === 0) return <Empty icon={Gift} text="Keine Treuepunkt-Bewegungen." />;
  return (
    <div data-testid="loyalty-list">
      <div className="mb-3 p-3 rounded-md flex items-center justify-between" style={{ background: "var(--mv-surface)" }}>
        <p className="text-xs uppercase tracking-wider opacity-70">Aktueller Saldo</p>
        <p className="font-display text-2xl">{balance} P.</p>
      </div>
      <ul className="divide-y" style={{ borderColor: "var(--mv-border)" }}>
        {transactions.map((t) => {
          const positive = (t.amount || 0) > 0;
          const Icon = positive ? TrendingUp : TrendingDown;
          return (
            <li key={t.id} className="py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.6} style={{ color: positive ? "#10B981" : "#DC2626" }} />
                <div className="min-w-0">
                  <p className="text-sm truncate">{t.reason || "—"}</p>
                  <p className="text-[11px] opacity-60">{formatDateTime(t.created_at)}{t.expires_at ? ` · läuft ab ${formatDate(t.expires_at)}` : ""}</p>
                </div>
              </div>
              <p className="text-sm font-display tabular-nums whitespace-nowrap" style={{ color: positive ? "#10B981" : "#DC2626" }}>
                {positive ? "+" : ""}{t.amount}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RedemptionsTab({ redemptions }) {
  if (redemptions.length === 0) return <Empty icon={Ticket} text="Keine eingelösten Belohnungen." />;
  return (
    <ul className="space-y-2" data-testid="redemptions-list">
      {redemptions.map((r) => (
        <li key={r.id} className="mv-card p-3 flex items-start justify-between gap-3">
          <div>
            <p className="font-medium text-sm">{r.reward_name}</p>
            <p className="text-xs opacity-70 mt-0.5">{r.points_cost} Punkte · {formatDateTime(r.created_at)}</p>
            <p className="text-xs mt-1.5 font-mono px-2 py-0.5 rounded inline-block" style={{ background: "var(--mv-surface)" }}>{r.code}</p>
          </div>
          <StatusPill status={r.status} />
        </li>
      ))}
    </ul>
  );
}

function NotesTab({ notes, setNotes, tagsInput, setTagsInput, onSave, saving, saved }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium mb-1.5 flex items-center gap-1.5">
          <FileText className="w-3 h-3" strokeWidth={1.6} /> Interne Notizen
        </label>
        <textarea
          data-testid="customer-notes"
          className="mv-input min-h-[160px]"
          placeholder="z. B. VIP-Kundin, Allergie gegen Ammoniak, bevorzugt Anna am Dienstag…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <p className="text-[11px] mt-1 opacity-60">Nur für Salon-Mitarbeiter sichtbar — nicht für den Kunden.</p>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1.5 flex items-center gap-1.5">
          <Tag className="w-3 h-3" strokeWidth={1.6} /> Tags
        </label>
        <input
          data-testid="customer-tags-input"
          className="mv-input"
          placeholder="VIP, Stammkunde, Allergie (mit Komma trennen)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-3 justify-end pt-2 border-t" style={{ borderColor: "var(--mv-border)" }}>
        {saved && <span className="text-xs" style={{ color: "#10B981" }} data-testid="customer-notes-saved">✓ Gespeichert</span>}
        <button onClick={onSave} disabled={saving} className="mv-btn text-sm" data-testid="customer-notes-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Speichere…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="py-12 text-center">
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-30" strokeWidth={1.2} />
      <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>{text}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    confirmed: { bg: "#D1FAE5", color: "#065F46", label: "Bestätigt" },
    completed: { bg: "#DBEAFE", color: "#1E3A8A", label: "Abgeschlossen" },
    pending: { bg: "#FEF3C7", color: "#92400E", label: "Offen" },
    cancelled: { bg: "#FEE2E2", color: "#991B1B", label: "Storniert" },
    no_show: { bg: "#F3F4F6", color: "#374151", label: "Nicht erschienen" },
  };
  const s = map[status] || { bg: "#F3F4F6", color: "#374151", label: status || "—" };
  return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}
function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
